import { AlertTriangle, Copy, Landmark } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { formatCurrency } from "@/lib/currency";


export const ModulePurchaseDialog = ({ module, open, onOpenChange, bankingDetails }) => {
  if (!module) {
    return null;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(module.payment_reference);
      toast.success("Reference copied");
    } catch {
      toast.error("Copy manually if needed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-stone-200 bg-[#fffdfa] p-0" data-testid="module-purchase-dialog">
        <div className="space-y-6 p-6 sm:p-7">
          <DialogHeader className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="module-purchase-dialog-eyebrow">Buy module by EFT</p>
            <DialogTitle className="text-[1.9rem] font-semibold leading-tight tracking-[-0.025em] text-slate-950" data-testid="module-purchase-dialog-title">{module.title}</DialogTitle>
            <DialogDescription className="text-sm leading-7 text-slate-600" data-testid="module-purchase-dialog-description">
              This module purchase is handled by manual EFT only. It is separate from the R150 application fee.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5 text-sm leading-7 text-slate-700" data-testid="module-purchase-dialog-coverage-card">
            This module purchase covers the registration fee for this specific module and access to the module learning material. After payment has been reviewed and approved, access to this module will be granted in your student portal.
          </div>

          <div className="flex flex-wrap items-center gap-3" data-testid="module-purchase-dialog-meta">
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700" data-testid="module-purchase-dialog-level">{module.level}</span>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700" data-testid="module-purchase-dialog-price">{formatCurrency(module.price)}</span>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700" data-testid="module-purchase-dialog-duration">{module.duration}</span>
          </div>

          <div className="rounded-[1.35rem] border border-stone-200 bg-[#fcfaf5] p-5 text-sm leading-7 text-slate-700" data-testid="module-purchase-dialog-banking-card">
            <div className="flex items-center gap-2 text-slate-950">
              <Landmark className="h-4 w-4" />
              <p className="font-semibold" data-testid="module-purchase-dialog-banking-title">Banking details</p>
            </div>
            <div className="mt-4 space-y-1.5">
              <p data-testid="module-purchase-dialog-bank-name">Bank: {bankingDetails?.bank_name}</p>
              <p data-testid="module-purchase-dialog-account-name">Account Name: {bankingDetails?.account_name}</p>
              <p data-testid="module-purchase-dialog-account-number">Account Number: {bankingDetails?.account_number}</p>
              <p data-testid="module-purchase-dialog-branch-code">Branch Code: {bankingDetails?.branch_code}</p>
            </div>
          </div>

          <div className="rounded-[1.45rem] border border-[#f3d5a3] bg-gradient-to-br from-[#fff7ec] via-[#fff2de] to-[#f5fbff] p-5 shadow-sm" data-testid="module-purchase-dialog-reference-card">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-600" data-testid="module-purchase-dialog-reference-label">EFT reference</p>
            <p className="mt-3 text-sm leading-7 text-slate-700" data-testid="module-purchase-dialog-reference-help">
              Please copy and paste this as your EFT reference: <span className="font-semibold text-slate-950">{module.payment_reference}</span>
            </p>
            <div className="mt-4 flex flex-col gap-3 rounded-[1.2rem] border border-white/70 bg-white/80 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[1.45rem] font-semibold tracking-[0.08em] text-slate-950" data-testid="module-purchase-dialog-reference-value">{module.payment_reference}</p>
              <Button type="button" variant="outline" className="rounded-full border-[#d7c6a9]" onClick={handleCopy} data-testid="module-purchase-dialog-copy-button">
                <Copy className="h-4 w-4" />
                Copy reference
              </Button>
            </div>
          </div>

          <div className="rounded-[1.35rem] border border-stone-200 bg-white p-5 text-sm leading-7 text-slate-700" data-testid="module-purchase-dialog-proof-card">
            <p className="font-semibold text-slate-950" data-testid="module-purchase-dialog-proof-title">Proof of payment</p>
            <p className="mt-2" data-testid="module-purchase-dialog-proof-body">After payment, please email your proof of payment to <span className="font-semibold text-slate-950">{bankingDetails?.proof_of_payment_email}</span>.</p>
            <p className="mt-2" data-testid="module-purchase-dialog-proof-approval-note">Module access will only be granted after payment is manually reviewed and approved.</p>
            <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500" data-testid="module-purchase-dialog-proof-safety-note">Opening these EFT instructions does not mark this module as paid.</p>
          </div>

          {module.chronology_warning_required ? (
            <div className="rounded-[1.35rem] border border-red-200 bg-red-50 p-5 text-sm leading-7 text-red-700" data-testid="module-purchase-dialog-warning-card">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-1 h-5 w-5 shrink-0" />
                <p data-testid="module-purchase-dialog-warning-text"><span className="font-semibold">Important:</span> You may purchase and access this module, but final assessments must be completed in module order. If you have not completed the earlier modules, you may be able to read this module, but you will not be eligible to complete its final online assessment until the required previous modules are completed.</p>
              </div>
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button type="button" variant="outline" className="rounded-full border-[#d7c6a9]" onClick={() => onOpenChange(false)} data-testid="module-purchase-dialog-close-button">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};